import React, { useContext, useState } from "react";
import {
  Card,
  Row,
  Col,
  Tag,
  Avatar,
  Button,
  Space,
  Divider,
  Popconfirm,
} from "antd";
import dayjs from "dayjs";
import {
  MessageOutlined,
  // ShareAltOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  generateColor,
  getInitials,
} from "Pages/MyBatches/Channels/ChannelComponents/ChannelComments/CommentPost";
import { ChannelCommentHome } from "Pages/MyBatches/Channels/ChannelComponents/ChannelComments/ChannelCommentHome";
import { IUserInfo } from "utilFunctions/CommonInterfaces/userInterface";
import { UserInfoContext } from "Context/CommonContext";
import Typography from "Components/CommonComponents/Typography";
import "./index.css";
import useMasterContext from "Context/MasterContext";
import UnreadIndicator from "./UnreadIndicator";
import QuestionTextProvider from "Components/CommonComponents/QuestionTextProvider";

interface Post {
  id?: number;
  title: string;
  description: string;
  updatedAt: string;
  status: string;
  subject?: string;
  createdBy?: string;
  creatorName?: string;
  commentCount?: number;
  likesCount?: number;
  postType?: string;
  batchId?: number;
  commentAllowed?: number;
}

interface SinglePostScreenProps {
  post: Post;
  allPosts: Post[];
  handlePostSelect: (post: Post) => void;
  handleEditPost: (post: Post) => void;
  handleDeletePost: (postId: string) => void;
  handleIncrementCommentCount: (postId: string) => void;
  handleDecrementCommentCount: (postId: string) => void;
  hasFullAccess?: boolean;
  onCommentsFetched?: (data: any) => void;
}

export const statusColor = (status?: string) => {
  const map: Record<string, string> = {
    PUBLISHED: "blue",
    DRAFT: "orange",
    SCHEDULED: "green",
  };
  return (status && map[status]) || "blue";
};

const SinglePostScreen: React.FC<SinglePostScreenProps> = ({
  post,
  handlePostSelect,
  handleEditPost,
  handleDeletePost,
  handleIncrementCommentCount,
  handleDecrementCommentCount,
  allPosts,
  hasFullAccess,
  onCommentsFetched,
}) => {
  const { masters } = useMasterContext();
  const { unreadComments } = masters || {};
  const batchId = post.batchId;
  const subject = post.subject;
  const subjectsWiseUnreadComments =
    unreadComments
      ?.find((ele: any) => ele.batchId === batchId)
      ?.subjects.find((comment: any) => comment.subject === subject) || {};
  const postIds = subjectsWiseUnreadComments?.postIds || [];
  const isPostUnread = post.id ? postIds.includes(post.id) : false;
  const userInfo = useContext<IUserInfo>(UserInfoContext);
  const [isCommentSectionVisible, setIsCommentSectionVisible] =
    React.useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  const description = post.description || "";
  const isLongDescription = description.length > 200;

  const truncateDescription = (text: string, maxLength: number) => {
    const truncated = text.substring(0, maxLength);
    const lastOpenMath = truncated.lastIndexOf("<math");
    const lastCloseMath = truncated.lastIndexOf("</math>");
    if (lastOpenMath > lastCloseMath) {
      const closeIdx = text.indexOf("</math>", lastOpenMath);
      const mathEnd = closeIdx !== -1 ? closeIdx + "</math>".length : -1;
      return mathEnd !== -1 ? text.substring(0, mathEnd) : truncated;
    }
    return truncated;
  };

  const truncatedDescription = isLongDescription
    ? truncateDescription(description, 200) + "..."
    : description;
  const isShow = true;
  return (
    <Card className="card-hover-custom card-body-responsive" bordered={false}>
      <Row justify="space-between" align="middle" className="mobile-header-row">
        <Col>
          <Space size="middle" align="start">
            <Avatar
              style={{
                backgroundColor: generateColor(post.creatorName),
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                border: "2px solid #fff",
              }}
              src={post.creatorName}
              size={44}
            >
              {getInitials(post.creatorName)}
            </Avatar>
            <div>
              <Space align="center" size="small" wrap>
                <Typography variant="h6" style={{ fontWeight: 600, margin: 0 }}>
                  {post.creatorName || "Anonymous"}
                </Typography>
                <Tag bordered={false} color={statusColor(post.status)}>
                  {post.status}
                </Tag>
                {post?.status === "PUBLISHED" && (
                  <UnreadIndicator
                    isUnread={isPostUnread}
                    color="blue"
                    size={8}
                    title={
                      isPostUnread ? "Unread comments" : "No unread comments"
                    }
                  />
                )}
              </Space>
              <div style={{ marginTop: -2 }}>
                <Typography className="timestamp-text">
                  {dayjs(post.updatedAt).format("DD MMM, YYYY")}
                </Typography>
              </div>
            </div>
          </Space>
        </Col>
        <Col className="mobile-action-col">
          <Space size="small">
            {post.status != "PUBLISHED" && hasFullAccess && (
              <Button
                type="text"
                shape="circle"
                icon={
                  <EditOutlined
                    style={{ fontSize: "18px", color: "#8c8c8c" }}
                  />
                }
                onClick={() => handleEditPost(post)}
                className="action-btn"
              />
            )}
            {hasFullAccess && (
              <Popconfirm
                title="Are you sure you want to delete this post?"
                onConfirm={() =>
                  post.id !== undefined && handleDeletePost(post.id.toString())
                }
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="text"
                  shape="circle"
                  danger
                  icon={<DeleteOutlined style={{ fontSize: "18px" }} />}
                  className="action-btn"
                />
              </Popconfirm>
              // <Button
              //   type="text"
              //   shape="circle"
              //   danger
              //   icon={<DeleteOutlined style={{ fontSize: "18px" }} />}
              //   onClick={() =>
              //     post.id !== undefined && handleDeletePost(post.id.toString())
              //   }
              //   className="action-btn"
              // />
            )}
          </Space>
        </Col>
      </Row>

      <div style={{ marginTop: 16 }}>
        <Typography variant="h5" className="post-title-text">
          {post.title}
        </Typography>
        <div style={{ marginTop: 8 }}>
          <Typography className="post-description-text">
            {isLongDescription ? (
              <>
                {isExpanded ? (
                  <QuestionTextProvider
                    content={description}
                  ></QuestionTextProvider>
                ) : (
                  <QuestionTextProvider
                    content={truncatedDescription}
                  ></QuestionTextProvider>
                )}
                <Button
                  type="link"
                  onClick={toggleExpanded}
                  style={{
                    padding: "0 0 0 4px",
                    height: "auto",
                    verticalAlign: "baseline",
                  }}
                >
                  {isExpanded ? "Show less" : "Show more"}
                </Button>
              </>
            ) : (
              <QuestionTextProvider
                content={description}
              ></QuestionTextProvider>
            )}
          </Typography>
        </div>
      </div>

      <Row justify="space-between" align="middle" style={{ marginTop: 20 }}>
        <Col xs={12} sm={12}>
          {post.postType == "POST" &&
            post.status == "PUBLISHED" &&
            post.commentAllowed &&
            isShow && (
              <div
                className="comment-icon-wrap comment-wrap-mobile"
                onClick={() =>
                  setIsCommentSectionVisible(!isCommentSectionVisible)
                }
                style={{ cursor: "pointer", display: "inline-flex" }}
              >
                <MessageOutlined
                  style={{ color: "#1890ff", fontSize: "18px" }}
                />
                <span
                  style={{ fontWeight: 500, fontSize: "14px", marginLeft: 8 }}
                  className="filterSpan"
                >
                  {post.commentCount ?? 0}
                </span>
              </div>
            )}
        </Col>
        <Col xs={12} sm={12} style={{ textAlign: "right" }}>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handlePostSelect(post)}
          >
            View
          </Button>
        </Col>
      </Row>

      {post?.postType == "POST" &&
        post?.status == "PUBLISHED" &&
        isCommentSectionVisible &&
        isShow && (
          <div style={{ animation: "fadeIn 0.3s ease-in-out" }}>
            <Divider style={{ margin: "24px 0 0 0" }} />
            <ChannelCommentHome
              selectedPost={post}
              allPosts={allPosts}
              userInfoid={userInfo.id}
              handleIncrementCommentCount={handleIncrementCommentCount}
              handleDecrementCommentCount={handleDecrementCommentCount}
              onCommentsFetched={onCommentsFetched}
            />
          </div>
        )}
    </Card>
  );
};

export default SinglePostScreen;
